/**
 * ITERCATUS — Consent helper (P1)
 *
 * Source de vérité côté client : localStorage.
 * Miroir côté serveur : POST /api/consents (best-effort, non bloquant).
 * Identité anonyme : `client_id` généré une fois, jamais lié au user connecté.
 *
 * Structure stockée :
 *   itercatus_consent_v1 = { purposes:{necessary,analytics,marketing,functional},
 *                            version, saved_at }
 *   itercatus_consent_client_id = "cid_..."
 */

const KEY = "itercatus_consent_v1";
const CLIENT_ID_KEY = "itercatus_consent_client_id";
const OPEN_EVENT = "itercatus:open-cookie-preferences";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

export const DEFAULT_PURPOSES = Object.freeze({
  necessary: true,
  analytics: false,
  marketing: false,
  functional: false,
});

export function getClientId() {
  try {
    let id = localStorage.getItem(CLIENT_ID_KEY);
    if (!id) {
      id =
        "cid_" +
        Math.random().toString(36).slice(2, 12) +
        Date.now().toString(36);
      localStorage.setItem(CLIENT_ID_KEY, id);
    }
    return id;
  } catch {
    return "cid_ephemeral_" + Date.now().toString(36);
  }
}

export function getConsent() {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || !parsed.purposes) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function saveConsentLocal(purposes) {
  const clean = { ...DEFAULT_PURPOSES, ...(purposes || {}), necessary: true };
  const payload = {
    purposes: clean,
    version: "v1",
    saved_at: new Date().toISOString(),
  };
  try {
    localStorage.setItem(KEY, JSON.stringify(payload));
  } catch {
    /* private mode — ignore */
  }
  return payload;
}

export async function recordConsent(purposes) {
  const clientId = getClientId();
  const local = saveConsentLocal(purposes);
  // best-effort server mirror (non bloquant)
  try {
    await fetch(`${BACKEND_URL}/api/consents`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        client_id: clientId,
        purposes: local.purposes,
        version: "v1",
      }),
      keepalive: true,
    });
  } catch {
    /* offline / blocked — localStorage remains source of truth */
  }
  // Notify any listeners (analytics loaders, etc.)
  try {
    window.dispatchEvent(
      new CustomEvent("itercatus:consent-changed", { detail: local.purposes })
    );
  } catch {}
  return local;
}

export function openCookiePreferences() {
  try {
    window.dispatchEvent(new CustomEvent(OPEN_EVENT));
  } catch {}
}

export function onOpenCookiePreferences(handler) {
  window.addEventListener(OPEN_EVENT, handler);
  return () => window.removeEventListener(OPEN_EVENT, handler);
}
